import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, fmtDate, getGuestToken, ESITO_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Predictions({ data }) {
  if (!data.pronostici.length) return <p className="text-muted-foreground">Nessuna scelta ancora.</p>;
  return (
    <ul className="space-y-3">
      {data.pronostici.map((p) => (
        <li key={p.dossier_id} className="luma-card text-sm" data-testid={`prediction-${p.dossier_id}`}>
          <Link to={`/dossier/${p.dossier_id}`} className="font-semibold hover:text-primary">{p.dossier_titolo}</Link>
          <p className="text-muted-foreground">Scelta: <b className="text-foreground">{p.theory_titolo}</b> · {ESITO_LABEL[p.esito]} · aggiornato {fmtDate(p.updated_at)}</p>
          {p.punti !== null && <p className={p.corretto ? "font-bold text-emerald-300" : "text-muted-foreground"}>{p.corretto ? "Corretto" : "Non corretto"} · {p.punti} punti</p>}
        </li>
      ))}
    </ul>
  );
}

function History({ data }) {
  return (
    <ul className="space-y-2 text-sm">
      {data.cronologia.map((h, i) => (
        <li key={i} className="rounded-xl border border-border p-3 text-muted-foreground">{fmtDate(h.at)} · {h.dossier_id}: {h.from ? `${h.from} → ` : ""}<b className="text-foreground">{h.to}</b></li>
      ))}
      {!data.cronologia.length && <p className="text-muted-foreground">Nessun cambio registrato.</p>}
    </ul>
  );
}

function Notifications() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/me/notifications").then((r) => setList(r.data)); }, []);
  const read = () => api.post("/me/notifications/read").then(() => setList(list.map((n) => ({ ...n, letto: true }))));
  return (
    <div className="space-y-3">
      <Button variant="outline" size="sm" onClick={read} data-testid="notifications-read-button" className="rounded-full">Segna tutte come lette</Button>
      {list.map((n) => (
        <div key={n.id} className={`luma-card text-sm ${n.letto ? "opacity-60" : "border-pink-400/50"}`} data-testid={`notification-${n.id}`}>
          <p>{n.testo}</p><p className="text-xs text-muted-foreground">{fmtDate(n.created_at)}</p>
        </div>
      ))}
      {!list.length && <p className="text-muted-foreground">Nessuna notifica.</p>}
    </div>
  );
}

function Proposals() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/me/proposals").then((r) => setList(r.data)); }, []);
  return (
    <ul className="space-y-2 text-sm">
      {list.map((p) => <li key={p.id} className="luma-card"><b>{p.titolo}</b> · <span className="text-muted-foreground">{p.stato.replace("_", " ")}</span></li>)}
      {!list.length && <p className="text-muted-foreground">Nessuna proposta inviata.</p>}
    </ul>
  );
}

function PasswordForm() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = () => api.post("/auth/change-password", f)
    .then(() => { toast.success("Password aggiornata"); setF({ current_password: "", new_password: "" }); })
    .catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-wrap gap-2" data-testid="change-password-form">
      <Input type="password" placeholder="Password attuale" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="max-w-xs rounded-xl" data-testid="current-password-input" />
      <Input type="password" placeholder="Nuova password (min 12)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="max-w-xs rounded-xl" data-testid="new-password-input" />
      <Button variant="outline" onClick={save} className="rounded-full" data-testid="change-password-button">Cambia password</Button>
    </div>
  );
}

function DeleteAccount() {
  const { setUser } = useAuth();
  const del = async () => {
    if (!window.confirm("Eliminare definitivamente l'account? I tuoi voti resteranno solo in forma anonima nelle percentuali.")) return;
    try { await api.delete("/me"); setUser(false); toast.success("Account eliminato"); } catch (e) { toast.error(errMsg(e)); }
  };
  return <button onClick={del} data-testid="delete-account-button" className="text-xs font-bold text-destructive hover:underline">Elimina il mio account e i miei dati</button>;
}

function UserHeader({ user, onClaim }) {
  const [sum, setSum] = useState(null);
  const [nome, setNome] = useState(user.nome);
  useEffect(() => { api.get("/me/summary").then((r) => setSum(r.data)); }, []);
  const save = () => api.put("/me", { nome }).then(() => toast.success("Nome aggiornato")).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="luma-card space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase text-muted-foreground">{user.role === "admin" ? "Lumacofono · Fondatore" : "Utente registrato"}</p>
          <h1 className="text-3xl font-bold" data-testid="profile-name">{user.nome}</h1>
        </div>
        <div className="text-right">
          <p data-testid="profile-points" className="font-display text-3xl font-bold text-primary">{sum?.punti ?? 0} punti</p>
          <p className="text-sm text-muted-foreground">{sum?.posizione ? `${sum.posizione}° in classifica` : "Non ancora in classifica"}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input value={nome} onChange={(e) => setNome(e.target.value)} className="max-w-xs rounded-xl" data-testid="profile-name-input" />
        <Button variant="outline" onClick={save} className="rounded-full" data-testid="profile-name-save">Salva nome pubblico</Button>
        {getGuestToken() && <Button onClick={onClaim} className="rounded-full bg-violet-500 text-white hover:bg-violet-400" data-testid="claim-guest-button">Associa le scelte anonime di questo browser</Button>}
      </div>
      <PasswordForm />
      {user.role !== "admin" && <DeleteAccount />}
      {sum?.rettifiche?.length > 0 && <p className="text-xs text-muted-foreground">Rettifiche punti: {sum.rettifiche.map((a) => `${a.delta > 0 ? "+" : ""}${a.delta} (${a.motivo})`).join("; ")}</p>}
    </div>
  );
}

export default function Profilo() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/me/predictions").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user !== null) load(); }, [user]);
  const claim = async () => {
    try {
      const r = await api.post("/auth/claim-guest");
      toast.success(`Associate ${r.data.associati} scelte (escluse ${r.data.esclusi}: misteri chiusi o già votati con l'account).`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (user === null || !data) return <p className="py-20 text-center text-muted-foreground">Caricamento…</p>;
  return (
    <div className="space-y-8">
      {user ? <UserHeader user={user} onClaim={claim} /> : (
        <div className="luma-card space-y-3" data-testid="guest-profile-banner">
          <h1 className="text-3xl font-bold">Il tuo profilo ospite</h1>
          <p className="text-sm text-muted-foreground">Le scelte qui sotto sono legate a un ID anonimo salvato solo in questo browser. Registrati (facoltativo) per sincronizzare lo storico, ricevere notifiche ed entrare in classifica.</p>
          <Link to="/login" data-testid="guest-register-link"><Button className="rounded-full">Registrati o accedi</Button></Link>
        </div>
      )}
      <Tabs defaultValue="pronostici">
        <TabsList className="flex-wrap rounded-full">
          <TabsTrigger value="pronostici" data-testid="profile-tab-predictions">Le mie scelte</TabsTrigger>
          <TabsTrigger value="cronologia" data-testid="profile-tab-history">Cronologia</TabsTrigger>
          {user && <TabsTrigger value="notifiche" data-testid="profile-tab-notifications">Notifiche</TabsTrigger>}
          <TabsTrigger value="proposte" data-testid="profile-tab-proposals">Proposte</TabsTrigger>
        </TabsList>
        <TabsContent value="pronostici" className="pt-4"><Predictions data={data} /></TabsContent>
        <TabsContent value="cronologia" className="pt-4"><History data={data} /></TabsContent>
        {user && <TabsContent value="notifiche" className="pt-4"><Notifications /></TabsContent>}
        <TabsContent value="proposte" className="pt-4"><Proposals /></TabsContent>
      </Tabs>
    </div>
  );
}
